<template>
    <div class="customer__form modal modal--default mandate-email-modal">
        <div class="modal__panel">
            <div class="modal__logo-wrapper">
                <div class="modal__logo"></div>
            </div>
            <div class="modal__main-icon modal__main-icon--email"></div>
            <div class="modal__close" @click="closeModal()"></div>

            <div class="form">
                <div class="navigators">
                    <div class="navigator navigator--active">
                        <div class="navigator__bullet"></div>
                        <div class="navigator__title">Send Mandate Email</div>
                    </div>
                </div>

                <div class="add__form">
                    <transition name="fade" mode="out-in">
                        <Loading v-if="isLoading" />
                        <div v-else class="add__content">
                            <div class="mandate-email-content">
                                <div class="form__field team-select-input">
                                    <MultiSelectDropdown :items="companies" placeholder="Select companies" label="name"
                                        :selected="emailData.companies" class="team-select"
                                        :class="{ 'input--error': companyHasError }"
                                        @input="onCompaniesSelected($event)" />
                                    <div class="form__error" v-if="companyHasError">
                                        Please select a company
                                    </div>
                                </div>

                                <div class="company-policies">
                                    <div v-for="company in emailData.companies" :key="company.id"
                                        class="company-policy-row">
                                        <div class="company-name-wrapper">
                                            <div class="company-name">
                                               
                                                <button @click="removeCompany(company.id)" type="button"
                                                    class="button remove-company-btn"
                                                    title="Remove this company">
                                                    <div class="button__icon"></div>
                                                </button>
                                                 <p>{{ company.name }}</p>
                                            </div>
                                        </div>

                                        <div class="policy-numbers">
                                            <div class="policy-numbers-container">
                                                <div v-for="(policyNum, index) in company.policyNumbers" :key="index"
                                                    class="policy-number-row">
                                                    <div class="form__field">
                                                        <input v-model="company.policyNumbers[index]" type="text"
                                                            class="input input--default" placeholder="Policy Nr."
                                                            @keydown.enter.prevent />
                                                    </div>

                                                    <button v-if="company.policyNumbers.length > 1"
                                                        @click="removePolicyNumber(company.id, index)" type="button"
                                                        class="button delete-policy-btn policy-btn">
                                                        <div class="button__icon"></div>
                                                    </button>
                                                </div>
                                            </div>
                                            <button @click="addPolicyNumber(company.id)"
                                                class="button button--default policy-btn add-policy-btn">
                                                <div class="button__icon"></div>
                                            </button>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div class="modal__buttons">
                <div v-if="isRequesting || isLoading"
                    class="modal__button modal__button--submit button button--default button--loading"></div>
                <button v-else class="modal__button modal__button--submit button button--default" @click="sendEmail()"
                    :disabled="isRequesting || isLoading">
                    <span>Send</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import Loading from "@/partials/Loading";
import MultiSelectDropdown from "@/partials/MultiSelectDropdown";

export default {
    name: "MandateEmailModal",
    components: {
        Loading,
        MultiSelectDropdown
    },
    computed: {
        ...mapGetters(["mandateEmailModal", "customerDetails", "mandateCompaniesForms"]),

        companies() {
            if (!this.mandateCompaniesForms) return [];
            return this.mandateCompaniesForms.companies || [];
        },
    },
    data() {
        return {
            emailData: {
                companies: [],
            },
            isLoading: false,
            isRequesting: false,
            companyHasError: false,
        };
    },
    async mounted() {
        this.isLoading = true;
        if (this.customerDetails.team) {
            this.teamId = Number(this.customerDetails.team.id);
            await this.$store.dispatch("getMandateCompaniesForms", this.teamId);
        }
        this.isLoading = false;
    },
    methods: {
        onCompaniesSelected(selectedCompanies) {
            const existingCompanyIds = new Set(this.emailData.companies.map(c => c.id));
            const selectedCompanyIds = new Set(selectedCompanies.map(c => c.id));

            // Remove companies that are no longer selected
            this.emailData.companies = this.emailData.companies.filter(c => selectedCompanyIds.has(c.id));

            // Add new companies with empty policy number arrays
            selectedCompanies.forEach(company => {
                if (!existingCompanyIds.has(company.id)) {
                    this.emailData.companies.push({
                        id: company.id,
                        name: company.name,
                        policyNumbers: [""]
                    });
                }
            });
        },

        addPolicyNumber(companyId) {
            const company = this.emailData.companies.find(c => c.id === companyId);
            if (company) {
                company.policyNumbers.push("");
            }
        },

        removePolicyNumber(companyId, index) {
            const company = this.emailData.companies.find(c => c.id === companyId);
            if (company) {
                company.policyNumbers.splice(index, 1);
            }
        },

        removeCompany(companyId) {
            this.emailData.companies = this.emailData.companies.filter(c => c.id !== companyId);
        },

        async sendEmail() {
            try {
                if (!this.emailData.companies || this.emailData.companies.length === 0) {
                    this.companyHasError = true;
                    this.$store.commit("setToast", {
                        display: true,
                        type: "error",
                        message: "Please select at least one company",
                    });
                    return;
                } else {
                    this.companyHasError = false;
                }

                this.isRequesting = true;

                const customer_id = this.$route.params.id;
                const mandate_id = this.mandateEmailModal.mandateId;

                const deliveries = this.emailData.companies.map(company => {
                    const policyNumbers = company.policyNumbers.filter(num => num.trim());
                    return {
                        company_id: company.id,
                        policy_numbers: policyNumbers.length > 0 ? policyNumbers : null
                    };
                });

                await this.$store.dispatch("sendMandateEmail", {
                    customer_id,
                    mandate_id,
                    deliveries,
                });

                await this.$store.dispatch("fetchMandates", customer_id);

                this.$store.commit("setToast", {
                    display: true,
                    type: "success",
                    message: "Email sent successfully",
                    
                });

                this.closeModal();
            } catch (error) {
                console.error("Error sending email:", error);
                this.$store.commit("setToast", {
                    display: true,
                    type: "error",
                    message: error.response?.data?.message || "Failed to send email",
                });
            } finally {
                this.isRequesting = false;
            }
        },

        closeModal() {
            this.$store.commit("setMandateEmailModal", {
                display: false,
                mandateId: null,
            });
            this.resetForm();
        },

        resetForm() {
            this.emailData = {
                companies: [],
            };
        },
    },
};
</script>

<style scoped lang="scss">
.modal__panel {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.form {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
}

.add__form {
    flex: 1;
    width: calc(100% - 40px);
    margin: 0 auto;
    overflow: hidden;
}

.add__content {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.mandate-email-content {
    width: 100%;
    margin: 0 auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.team-select-input {
    width: 100%;
    margin: 0 auto;
    margin-bottom: 40px;
    flex-shrink: 0;
}

.company-policies {
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
    flex: 1;
}

.company-policy-row {
    display: flex;
    align-items: flex-start;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--bg-2);

    &:last-child {
        border-bottom: none;
        padding-bottom: 0px;
    }

    .company-name-wrapper {
        width: 50%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 8px;
    }

    .company-name {
        display: flex;
        align-items: center;
        gap: 10px;
        flex: 1;

        p {
            color: var(--text-2);
            font-size: 16px;
        }

        button{
            transform: scale(0.8);
        }
    }

    .policy-numbers {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        width: 50%;
    }

    .policy-numbers-container {
        width: 90%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;

        .policy-number-row {
            display: flex;
            gap: 5px;
            width: 100%;

            .form__field {
                width: 90%;
                margin-bottom: 0;
                input {
                    width: 100%;
                }
            }
        }
    }

    .policy-btn {
        transform: scale(0.7);
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        .button__icon {
            margin: auto;
            width: 20px;
            height: 20px;
            background-image: url("../assets/icons/default/add_icon.svg");
            background-repeat: no-repeat;
            background-position: center;
        }
    }

    .delete-policy-btn {
        background-color: #f44336;

        &:hover {
            background-color: #da190b;
            box-shadow: 0px -7px 18px rgba(0, 0, 0, 0.2) inset;
        }

        .button__icon {
            transform: rotate(45deg) !important;
        }
    }

    .remove-company-btn {
        background-color: #f44336;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &:hover {
            background-color: #da190b;
            box-shadow: 0px -7px 18px rgba(0, 0, 0, 0.2) inset;
        }

        .button__icon {
            width: 16px;
            height: 16px;
            background-image: url("../assets/icons/default/add_icon.svg");
            background-repeat: no-repeat;
            background-position: center;
            transform: rotate(45deg);
        }
    }
}
</style>
